/**
 * App-wide feature flags (Jun 18).
 *
 * SHOW_LEGACY_TEMPLATES — gates the 90 legacy scene/AI templates +
 * recreate templates across the app (Product Shot / Carousel / Product
 * Swap Templates tabs + the /templates side-nav page). Recipes
 * ("moodboard recipes as templates") are the replacement.
 *
 * IMPORTANT: this only HIDES the legacy templates from the UI — it does
 * NOT delete any data. All legacy template assets + registries stay in
 * the repo (mocks/ai-templates.ts, mocks/demo.ts scene groups,
 * recreate-templates.json, backend/templates/, backend/ai_templates.py).
 * Flip this back to `true` and every legacy template returns instantly.
 * Some legacy shots are genuinely valuable — this keeps them retrievable.
 *
 * Rollout: Product Shot is hidden first (it already has recipes wired).
 * Carousel + Product Swap keep legacy until their recipe paths land, so
 * they don't end up with empty Templates tabs (see SHOW_LEGACY_FOR_ROUTE).
 */
export const SHOW_LEGACY_TEMPLATES = false

/** Per-route legacy gate. As of Jun 18 ALL studio routes (product-shot,
 *  fashion, carousel, product-swap) have recipe pickers wired, so this set
 *  is empty — every route is recipes-only. Add a route back here (with the
 *  flag off) only if you need its legacy AI grid temporarily. Flip
 *  SHOW_LEGACY_TEMPLATES to bring legacy back everywhere at once. */
const LEGACY_RETAINED_ROUTES = new Set<string>([])

export function showLegacyForRoute(category: string): boolean {
  if (SHOW_LEGACY_TEMPLATES) return true
  return LEGACY_RETAINED_ROUTES.has(category)
}

/**
 * V1 launch WITHOUT Facebook/Instagram integration (Jul 31).
 *
 * FB App Review isn't approved yet, so V1 ships with social publishing +
 * connect + analytics presented as "coming soon" rather than half-working.
 * Like SHOW_LEGACY_TEMPLATES above, these only HIDE the UI — every FB/IG
 * component, route, hook, and backend endpoint stays in the repo. Flip a
 * flag back to `true` (after App Review + the prod FB-app cutover) and the
 * feature returns instantly, no rebuild.
 *
 * SOCIAL_PUBLISHING_ENABLED — the FB/IG surface: sidebar connect rows,
 *   OAuth connect, "Post to Facebook/Instagram" buttons (gallery +
 *   creative editor), and the /analytics/{facebook,instagram} pages.
 *   When false, the sidebar shows Instagram + Facebook as "coming soon"
 *   (disabled) and all publish/connect/analytics entry points are hidden.
 *
 * SCHEDULING_ENABLED — the Ad Calendar + Ad Pilot suite. They exist to
 *   publish to FB/IG, so with no social target they're marked "coming
 *   soon" in the sidebar (per the V1 decision) rather than removed.
 */
// Aug 19 2026 — flipped to false for the public launch: FB/IG publishing +
// Ad Pilot/Calendar are HIDDEN until Meta App Review approves the two publish
// permissions (pages_manage_posts + instagram_content_publish). Flip both back
// to true after approval + the FB-app is Live — no rebuild of logic, just this.
export const SOCIAL_PUBLISHING_ENABLED = true
export const SCHEDULING_ENABLED = true

/**
 * Per-account override for the social + scheduling surface (Aug 20).
 *
 * The two globals above are the DEFAULT for real users (false = hidden while
 * Meta App Review is pending). These helpers additionally turn the FB/IG
 * surface ON for specific accounts — the Meta reviewer (so they can test the
 * publish/connect/analytics flow the review covers) and the internal team (so
 * we can dogfood it) — WITHOUT exposing publishing to paying users.
 *
 * When Meta approves + the FB app goes Live, flip the two globals to `true`
 * and this allowlist becomes a harmless no-op (the global short-circuits).
 *
 * Consumers must read these through the account's email — either the hooks in
 * hooks/useFeatureFlags.ts (inside components) or these fns directly.
 */
const SOCIAL_ALLOW_EMAILS = new Set<string>([
  'meta-reviewer@adastronaut.com', // Meta App Review reviewer
])
const SOCIAL_ALLOW_DOMAINS = new Set<string>([
  'zaah.com', // internal team / admins
])

function isSocialAllowlisted(email?: string | null): boolean {
  if (!email) return false
  const e = email.toLowerCase().trim()
  if (SOCIAL_ALLOW_EMAILS.has(e)) return true
  const domain = e.split('@')[1]
  return domain ? SOCIAL_ALLOW_DOMAINS.has(domain) : false
}

/** Social publish/connect/analytics enabled for this account: the global flag
 *  OR the account is on the reviewer/admin allowlist. */
export function socialPublishingEnabledForEmail(email?: string | null): boolean {
  return SOCIAL_PUBLISHING_ENABLED || isSocialAllowlisted(email)
}

/** Ad Calendar + Ad Pilot enabled for this account (same allowlist as social —
 *  they auto-publish to FB/IG, so the reviewer/team get the full flow). */
export function schedulingEnabledForEmail(email?: string | null): boolean {
  return SCHEDULING_ENABLED || isSocialAllowlisted(email)
}
